import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalTransaction } from '../utils/TransactionFormatters';
import { TransactionTableHeader, ColumnsRecord } from './TransactionTableHeader';
import { TransactionRow } from './TransactionRow';
import { Button } from '../../ui/button';
import type { SortField } from '@/types/base';

interface OptimizedTransactionTableProps {
  transactions: LocalTransaction[];
  columnOrder: string[];
  transactionColumns: ColumnsRecord;
  columnWidths: Record<string, string>;
  selectedTransactions: Set<string>;
  totalItems: number;
  isLoading: boolean;
  hasMoreData: boolean;
  pageSize: number;
  sortField: SortField;
  sortDirection: 'asc' | 'desc';
  isColumnSorting: boolean;
  groupBy: string | null;
  accounts: any[];
  categories: any[];
  tags: any[];
  loadingMoreText: string;
  
  // Event handlers
  onToggleAllTransactions: () => void;
  onToggleTransactionSelection: (transactionId: string) => void;
  onSort: (field: SortField) => void;
  onResizeStart: (e: React.MouseEvent<HTMLDivElement>, columnId: string) => void;
  onGroupBy: (field: string | null) => void;
  onEdit: (transaction: LocalTransaction) => void;
  onDelete: (transactionId: string) => void;
  onLoadMore: () => void;
}

// Batch size for rendering optimization
const RENDER_BATCH_SIZE = 50;
const RENDER_DELAY = 16; // ~60fps

export const OptimizedTransactionTable: React.FC<OptimizedTransactionTableProps> = ({
  transactions,
  columnOrder,
  transactionColumns,
  columnWidths,
  selectedTransactions,
  totalItems,
  isLoading,
  hasMoreData,
  pageSize,
  sortField,
  sortDirection,
  isColumnSorting,
  groupBy,
  accounts,
  categories,
  tags,
  loadingMoreText,
  onToggleAllTransactions,
  onToggleTransactionSelection,
  onSort,
  onResizeStart,
  onGroupBy,
  onEdit,
  onDelete,
  onLoadMore
}) => {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const lastLoadTimeRef = useRef<number>(0);
  const [renderCount, setRenderCount] = React.useState(RENDER_BATCH_SIZE);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const loadMoreTrigger = loadMoreTriggerRef.current;
    const scrollContainer = scrollContainerRef.current;
    
    if (!loadMoreTrigger || !hasMoreData || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        
        if (entry.isIntersecting) {
          const now = Date.now();
          const timeSinceLastLoad = now - lastLoadTimeRef.current;
          
          // More conservative approach: increase threshold and add transaction count check
          const shouldLoad = timeSinceLastLoad > 3000 && // Increased to 3 seconds
                           hasMoreData && 
                           !isLoading &&
                           transactions.length < 2000; // Don't auto-load beyond 2000 transactions
          
          if (shouldLoad) {
            lastLoadTimeRef.current = now;
            onLoadMore();
          }
        }
      },
      {
        root: scrollContainer,
        rootMargin: '100px 0px', // Reduced from 200px to be less aggressive
        threshold: 0.1
      }
    );

    observer.observe(loadMoreTrigger);

    return () => {
      observer.unobserve(loadMoreTrigger);
      observer.disconnect();
    };
  }, [hasMoreData, isLoading, onLoadMore, transactions.length]);

  // Progressive rendering effect
  useEffect(() => {
    if (renderCount < transactions.length) {
      const timeoutId = setTimeout(() => {
        setRenderCount(prev => Math.min(prev + RENDER_BATCH_SIZE, transactions.length));
      }, RENDER_DELAY);

      return () => clearTimeout(timeoutId);
    }
  }, [renderCount, transactions.length]);

  // Adjust render count based on transaction changes
  useEffect(() => {
    if (transactions.length === 0) {
      // No transactions - reset to batch size
      setRenderCount(RENDER_BATCH_SIZE);
    } else if (transactions.length < renderCount * 0.5) {
      // Significant decrease (filter change, data reset) - reset to batch size
      setRenderCount(RENDER_BATCH_SIZE);
    } else if (transactions.length > renderCount) {
      // More transactions available than currently rendered - expand to show all
      setRenderCount(transactions.length);
    }
  }, [transactions.length, renderCount]);

  /**
   * Group transactions by specified field - memoized for performance
   */
  const groupedTransactions = useMemo(() => {
    const transactionsToRender = transactions.slice(0, renderCount);
    
    if (!groupBy) return { 'all': transactionsToRender };
    
    return transactionsToRender.reduce((groups: Record<string, LocalTransaction[]>, transaction) => {
      let groupKey: string;
      
      switch (groupBy) {
        case 'date':
          groupKey = transaction.date.toDateString();
          break;
        case 'category':
          groupKey = transaction._categoryDisplayName || 'Uncategorized';
          break;
        case 'account':
          groupKey = transaction._accountDisplayName || 'Unknown Account';
          break;
        case 'amount':
          groupKey = transaction.amount > 0 ? 'Income' : 'Expense';
          break;
        default:
          groupKey = 'all';
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(transaction);
      
      return groups;
    }, {});
  }, [transactions, groupBy, renderCount]);

  /**
   * Memoized data for transaction rows to prevent unnecessary re-renders
   */
  const memoizedData = useMemo(() => ({
    accounts,
    categories,
    tags,
    columnOrder,
    transactionColumns,
    selectedTransactions,
    onToggleTransactionSelection,
    onEdit,
    onDelete
  }), [
    accounts,
    categories,
    tags,
    columnOrder,
    transactionColumns,
    selectedTransactions,
    onToggleTransactionSelection,
    onEdit,
    onDelete
  ]);

  /**
   * Render table body with optimized rendering
   */
  const renderTableBody = useCallback((): JSX.Element => {
    if (transactions.length === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={columnOrder.length + 2} className="p-12 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="rounded-full bg-info/10 p-3 mb-3">
                  <div className="h-8 w-8 text-info" />
                </div>
                <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary">
                  {t('transactions.noTransactions')}
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1 max-w-md text-center">
                  {t('transactions.noTransactionsDescription')}
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody>
        {Object.entries(groupedTransactions).map(([groupKey, groupTransactions]) => (
          <React.Fragment key={groupKey}>
            {groupBy && groupKey !== 'all' && (
              <tr className="group-header">
                <td colSpan={columnOrder.length + 2} className="p-3 bg-gray-50 dark:bg-gray-800 font-medium text-sm">
                  {groupKey} ({groupTransactions.length} transactions)
                </td>
              </tr>
            )}
                         {groupTransactions.map(transaction => (
               <TransactionRow
                 key={transaction.id}
                 transaction={transaction}
                 columnOrder={memoizedData.columnOrder}
                 transactionColumns={memoizedData.transactionColumns}
                 isSelected={memoizedData.selectedTransactions.has(transaction.id)}
                 accounts={memoizedData.accounts}
                 categories={memoizedData.categories}
                 tags={memoizedData.tags}
                 onToggleSelection={memoizedData.onToggleTransactionSelection}
                 onEdit={memoizedData.onEdit}
                 onDelete={memoizedData.onDelete}
               />
             ))}
          </React.Fragment>
        ))}
        
        {/* Progressive loading indicator */}
        {renderCount < transactions.length && (
          <tr>
            <td colSpan={columnOrder.length + 2} className="p-3 text-center border-t">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Loading more transactions... ({renderCount} of {transactions.length})
                </span>
              </div>
            </td>
          </tr>
        )}
        
        {/* Infinite scroll trigger element - always show when we have more data, regardless of render count */}
        {hasMoreData && (
          <tr>
            <td colSpan={columnOrder.length + 2}>
              <div 
                ref={loadMoreTriggerRef}
                className="h-1 w-full"
                style={{ visibility: 'hidden' }}
              />
            </td>
          </tr>
        )}
      </tbody>
    );
  }, [
    transactions.length,
    columnOrder.length,
    groupedTransactions,
    groupBy,
    memoizedData,
    renderCount,
    hasMoreData,
    t
  ]);

  return (
    <div className="transactions-table-wrapper">
      <div className="transactions-table-container">
        <div className="transactions-scroll-container" ref={scrollContainerRef}>
          <table className="transactions-table" ref={tableRef}>
            <thead>
              <TransactionTableHeader
                columnOrder={columnOrder}
                transactionColumns={transactionColumns}
                columnWidths={columnWidths}
                selectedTransactions={selectedTransactions}
                totalTransactions={transactions.length}
                sortField={sortField}
                sortDirection={sortDirection}
                isColumnSorting={isColumnSorting}
                groupBy={groupBy}
                onToggleAllTransactions={onToggleAllTransactions}
                onSort={onSort}
                onResizeStart={onResizeStart}
                onGroupBy={onGroupBy}
              />
            </thead>
            
            {renderTableBody()}
          </table>
        </div>
        
        {/* Footer with pagination info and load more button */}
        <div className="transactions-footer">
          <div>
            {totalItems === 0 
              ? t('transactions.noTransactions') 
              : `${t('transactions.pagination.showing')} ${Math.min(renderCount, transactions.length)} ${t('transactions.pagination.of')} ${totalItems} ${t('transactions.pagination.transactions')}`}
            {isLoading && (
              <span className="ml-2 inline-flex items-center">
                <div className="loading-spinner" style={{ width: '0.75rem', height: '0.75rem' }}></div>
                <span className="ml-1">{loadingMoreText}</span>
              </span>
            )}
            {transactions.length >= 2000 && hasMoreData && !isLoading && (
              <span className="ml-2 text-orange-600 text-sm">
                (Auto-loading paused at 2000+ transactions for performance)
              </span>
            )}
          </div>
          {hasMoreData && !isLoading && transactions.length < 2000 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onLoadMore}
              className="text-primary hover:text-primary/80"
            >
              {t('transactions.loadMore', 'Load more')} ({pageSize})
            </Button>
          )}
          {hasMoreData && !isLoading && transactions.length >= 2000 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onLoadMore}
              className="text-orange-600 hover:text-orange-700"
            >
              Continue Loading ({pageSize} more)
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}; 